import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { ReportGenUser } from "../../../shared/interfaces/User";

export interface UserStatusDialogProps {
  open: boolean;
  action: "activate" | "deactivate" | null;
  user: ReportGenUser | null;
  isUpdating: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const UserStatusDialog: React.FC<UserStatusDialogProps> = ({
  open,
  action,
  user,
  isUpdating,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();

  const handleClose = () => {
    if (!isUpdating) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (action && !isUpdating) {
      void onConfirm();
    }
  };

  const isActivate = action === "activate";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 20px 60px ${alpha(theme.palette.common.black, 0.2)}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: isActivate
              ? alpha(theme.palette.success.main, 0.1)
              : alpha(theme.palette.error.main, 0.1),
            color: isActivate ? "success.main" : "error.main",
          }}
        >
          {isActivate ? <ToggleOnIcon /> : <ToggleOffIcon />}
        </Box>
        <Typography variant="h6" fontWeight={600}>
          {isActivate ? "Reactivate User" : "Deactivate User"}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography color="text.secondary">
          Are you sure you want to{" "}
          {isActivate ? "reactivate" : "deactivate"}{" "}
          <Box component="span" fontWeight={600} color="text.primary">
            {user ? `${user.firstName} ${user.lastName}` : "this user"}
          </Box>
          ?
        </Typography>
        {!isActivate && (
          <Alert
            severity="warning"
            sx={{ mt: 2, borderRadius: 2 }}
            icon={<WarningIcon fontSize="small" />}
          >
            This user will no longer be able to access the system.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={isUpdating}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            px: 2.5,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isActivate ? "success" : "error"}
          disabled={isUpdating || !action}
          startIcon={isActivate ? <ToggleOnIcon /> : <ToggleOffIcon />}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            boxShadow: "none",
            "&:hover": {
              boxShadow: `0 4px 12px ${alpha(
                isActivate
                  ? theme.palette.success.main
                  : theme.palette.error.main,
                0.3
              )}`,
            },
          }}
        >
          {isUpdating ? "Working..." : isActivate ? "Reactivate" : "Deactivate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserStatusDialog;
